import { makeAutoObservable } from 'mobx'
import { type IActiveCampaignSettingsDict, type Integration } from 'entities/Integrations'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import { IntegrationCollapseSettingsStore } from 'pages/settings/pages/integrations/pages/integrationSettings/store/IntegrationCollapseSettingsStore'
import { IntegrationSettingsUsersStore } from 'pages/settings/pages/integrations/pages/integrationSettings/store/IntegrationSettingsUsersStore'
import { IntegrationCustomSettingsStore } from 'pages/settings/pages/integrations/pages/integrationSettings/store/IntegrationCustomSettingsStore'
import { IntegrationSettingsPropertyStore } from 'pages/settings/pages/integrations/pages/integrationSettings/store/IntegrationSettingsPropertyStore'
import { IntegrationWebhookStore } from 'pages/settings/pages/integrations/store/IntegrationWebhookStore'
import { ACTIVE_CAMPAIGN_SYNC_CHECKBOX_KEYS } from 'pages/settings/pages/integrations/pages/integrationSettings/IntegrationActiveCampaignSettings/lib'
import { IntegrationSettingsSyncStore } from 'pages/settings/pages/integrations/pages/integrationSettings/store/IntegrationSettingsSyncStore'
import { IntegrationTimelineStore } from 'pages/settings/pages/integrations/pages/integrationSettings/store/IntegrationTimelineStore'
import { type IIntegrationStore } from 'pages/settings/pages/integrations/pages/integrationSettings/hooks/useIntegrationStoreMemo'

export class IntegrationActiveCampaignSettingsStore implements IIntegrationStore {
  private _collapseStore
  private _userStore
  private _customSettingsStore
  private _syncStore
  private _propertyStore
  private _timelineStore
  private _webhookStore

  constructor(integration: Integration, private _entities: IEntitiesStore) {
    this._collapseStore = new IntegrationCollapseSettingsStore()
    this._userStore = new IntegrationSettingsUsersStore(integration, this._entities)
    this._customSettingsStore = new IntegrationCustomSettingsStore<IActiveCampaignSettingsDict>(
      integration
    )
    this._syncStore = new IntegrationSettingsSyncStore<IActiveCampaignSettingsDict>(
      integration,
      this._customSettingsStore,
      ACTIVE_CAMPAIGN_SYNC_CHECKBOX_KEYS
    )
    this._propertyStore = new IntegrationSettingsPropertyStore(
      integration,
      this._customSettingsStore,
      this._entities
    )
    this._timelineStore = new IntegrationTimelineStore(this._entities, 'activecampaign')
    this._webhookStore = new IntegrationWebhookStore(this._entities)

    makeAutoObservable(this)
  }

  init = (integration: Integration) => {
    this._userStore.init()
    this._timelineStore.init()
    this._customSettingsStore.init()
    this._propertyStore.init()
    this._syncStore.init()
    this._webhookStore.initIntegrationWebhookState(integration)
  }

  dispose = () => {
    this._syncStore.dispose()
    this._propertyStore.dispose()
    this._userStore.dispose()
    this._webhookStore.dispose()
  }

  get collapseStore() {
    return this._collapseStore
  }

  get userStore() {
    return this._userStore
  }

  get syncStore() {
    return this._syncStore
  }

  get propertyStore() {
    return this._propertyStore
  }

  get timelineStore() {
    return this._timelineStore
  }

  get webhookStore() {
    return this._webhookStore
  }
}
